import React, { useRef, useState, useCallback, useEffect, forwardRef, useImperativeHandle } from 'react';
import { Editor } from '@tinymce/tinymce-react';
import { ContentBlock } from '../../types';
import { parseHtmlToBlocks } from '../../utils/contentBlocks';

function blocksToHtml(blocks: ContentBlock[]): string {
  if (!blocks || blocks.length === 0) return '';
  return blocks
    .map((b) => {
      if (b.type === 'image') {
        const alt = (b.meta?.queryText || '').replace(/"/g, '&quot;');
        return `<p style="text-align:center"><img src="${b.content}" alt="${alt}" style="max-width:100%;height:auto" /></p>`;
      }
      return b.content || '';
    })
    .join('\n');
}

export interface TinyMCEEditorHandle {
  insertImage: (url: string, alt?: string) => void;
}

interface TinyMCEEditorProps {
  blocks: ContentBlock[];
  activeChapterId?: string | null;
  onContentChange?: (blocks: ContentBlock[]) => void;
  onImageInsert?: () => void;
  onImageReplace?: (alt: string) => void;
  isGenerating?: boolean;
  readOnly?: boolean;
}

const TinyMCEEditor = forwardRef<TinyMCEEditorHandle, TinyMCEEditorProps>(({
  blocks,
  onContentChange,
  onImageInsert,
  onImageReplace,
  isGenerating = false,
  readOnly = false,
}, ref) => {
  const editorRef = useRef<any>(null);
  const bookmarkRef = useRef<any>(null);
  const programmaticRef = useRef(false);
  const [initialContent, setInitialContent] = useState('');

  // Build initial HTML from blocks
  useEffect(() => {
    const html = blocksToHtml(blocks);
    setInitialContent(html);
  }, [blocks]);

  // When blocks change externally (e.g. streaming), update editor
  useEffect(() => {
    if (editorRef.current && blocks && blocks.length > 0) {
      const html = blocksToHtml(blocks);
      const current = editorRef.current.getContent();
      if (html !== current) {
        programmaticRef.current = true;
        editorRef.current.setContent(html);
        // Auto-scroll to bottom during generation
        if (isGenerating) {
          requestAnimationFrame(() => {
            try {
              const ed = editorRef.current;
              if (!ed) return;
              const doc = ed.getDoc();
              if (doc && doc.documentElement) {
                doc.documentElement.scrollTop = doc.documentElement.scrollHeight;
              }
            } catch (_) {}
          });
        }
        requestAnimationFrame(() => { programmaticRef.current = false; });
      }
    }
  }, [blocks, isGenerating]);

  const handleInit = useCallback((_evt: any, editor: any) => {
    editorRef.current = editor;
  }, []);

  const handleChange = useCallback((_value: string, editor: any) => {
    if (!onContentChange || programmaticRef.current) return;
    const html = editor.getContent();
    const parsed = parseHtmlToBlocks(html);
    onContentChange(parsed);
  }, [onContentChange]);

  useImperativeHandle(ref, () => ({
    insertImage(url: string, alt?: string) {
      if (editorRef.current) {
        const editor = editorRef.current;
        // Restore saved bookmark (cursor/image position) before inserting
        if (bookmarkRef.current) {
          try {
            editor.selection.moveToBookmark(bookmarkRef.current);
          } catch (_) {}
          bookmarkRef.current = null;
        }
        const imgHtml = `<img src="${url}" alt="${alt || ''}" style="max-width:100%;height:auto" />`;
        editor.insertContent(imgHtml);
        editor.fire('change');
      }
    },
  }));

  return (
    <div className="mx-auto h-full flex flex-col" style={{ maxWidth: '900px', width: '100%' }}>
      <Editor
        tinymceScriptSrc="https://cdn.jsdelivr.net/npm/tinymce@8/tinymce.min.js"
        onInit={handleInit}
        initialValue={initialContent}
        onChange={handleChange}
        disabled={readOnly || isGenerating}
        init={{
          license_key: 'gpl',
          height: '100%',
          menubar: true,
          plugins: [
            'advlist', 'autolink', 'lists', 'link', 'image', 'charmap', 'preview',
            'anchor', 'searchreplace', 'visualblocks', 'code', 'fullscreen',
            'insertdatetime', 'media', 'table', 'help', 'wordcount',
          ],
          toolbar:
            'undo redo | blocks fontfamily fontsize | bold italic underline strikethrough superscript subscript | ' +
            'forecolor backcolor | alignleft aligncenter alignright alignjustify | ' +
            'bullist numlist outdent indent | lineheight | ' +
            'table tabledelete | tableprops tablerowprops tablecellprops | ' +
            'tableinsertrowbefore tableinsertrowafter tabledeleterow | ' +
            'tableinsertcolbefore tableinsertcolafter tabledeletecol | ' +
            'searchreplace | link customimage | removeformat | fullscreen | help',
          font_family_formats:
            '宋体=SimSun;仿宋=FangSong;楷体=KaiTi;黑体=SimHei;微软雅黑=Microsoft YaHei;Arial=arial,helvetica,sans-serif;Times New Roman=times new roman,times,serif',
          font_size_formats: '8pt 9pt 10pt 11pt 12pt 14pt 16pt 18pt 20pt 24pt 28pt 36pt 48pt',
          lineheight_formats: '1 1.15 1.5 2 2.5 3',
          content_style: `
            body { font-family: SimSun, FangSong, KaiTi, serif; font-size: 12pt; line-height: 1.8; color: #1e293b; padding: 16pt 56pt 40pt 56pt; }
            p { margin: 0 0 10px 0; text-indent: 2em; }
            h1, h2, h3 { text-indent: 0; }
            table { border-collapse: collapse; width: 100%; margin: 12px 0; }
            td, th { border: 1px solid #cbd5e1; padding: 8px 12px; }
            th { background: #f1f5f9; font-weight: 600; }
            img { max-width: 100%; height: auto; }
            blockquote { border-left: 3px solid #cbd5e1; padding-left: 1em; color: #475569; margin: 12px 0; }
          `,
          language: 'zh_CN',
          language_url: window.location.origin + '/tinymce/langs/zh_CN.js',
          promotion: false,
          branding: false,
          images_file_types: 'jpg,jpeg,png,gif,webp',
          table_advtab: true,
          table_default_attributes: { border: '1' },
          table_default_styles: { 'border-collapse': 'collapse', width: '100%' },
          table_appearance_options: true,
          table_grid: true,
          table_tab_navigation: true,
          link_default_target: '_blank',
          link_title: false,
          relative_urls: false,
          remove_script_host: false,
          convert_urls: true,
          image_advtab: false,
          image_title: true,
          setup: (editor: any) => {
            // Register custom image button in toolbar
            editor.ui.registry.addButton('customimage', {
              icon: 'image',
              tooltip: '插入图片',
              onAction: () => {
                // Save cursor position before opening modal (editor loses focus)
                bookmarkRef.current = editor.selection.getBookmark(2, true);
                if (onImageInsert) onImageInsert();
              },
            });

            // Override right-click context menu for images
            editor.on('init', () => {
              // Override built-in mceImage command (must be inside init, after plugins loaded)
              editor.addCommand('mceImage', () => {
                bookmarkRef.current = editor.selection.getBookmark(2, true);
                if (onImageInsert) onImageInsert();
              });

              const doc = editor.getDoc();
              if (!doc) return;

              doc.addEventListener('contextmenu', (e: MouseEvent) => {
                const target = e.target as HTMLElement;
                if (!target || target.nodeName !== 'IMG') return;

                e.preventDefault();
                e.stopPropagation();
                e.stopImmediatePropagation();

                const existing = document.getElementById('tinymce-img-contextmenu');
                if (existing) existing.remove();

                const menu = document.createElement('div');
                menu.id = 'tinymce-img-contextmenu';
                menu.style.cssText =
                  'position:fixed;z-index:99999;background:#fff;border:1px solid #e2e8f0;' +
                  'border-radius:8px;box-shadow:0 4px 16px rgba(0,0,0,0.12);padding:4px;min-width:150px;';

                const items = [
                  { label: '替换图片', action: () => {
                    const img = target as HTMLImageElement;
                    const alt = img.alt || img.getAttribute('data-description') || '';
                    // Save bookmark around the image, then remove it silently
                    editor.selection.select(img);
                    bookmarkRef.current = editor.selection.getBookmark(2, true);
                    img.remove();
                    // Don't fire 'change' here — let insertImage handle it,
                    // otherwise React re-render may call setContent and destroy the bookmark
                    if (onImageReplace) onImageReplace(alt);
                    else if (onImageInsert) onImageInsert();
                  } },
                  { label: '删除图片', action: () => { target.remove(); editor.fire('change'); } },
                ];

                items.forEach((item) => {
                  const d = document.createElement('div');
                  d.textContent = item.label;
                  d.style.cssText =
                    'padding:8px 12px;cursor:pointer;font-size:13px;color:#1e293b;border-radius:4px;';
                  d.onmouseenter = () => { d.style.background = '#f1f5f9'; };
                  d.onmouseleave = () => { d.style.background = ''; };
                  d.onclick = () => { item.action(); menu.remove(); };
                  menu.appendChild(d);
              });

                const iframe = editor.getContentAreaContainer()?.querySelector('iframe');
                const rect = iframe?.getBoundingClientRect();
                const x = rect ? e.clientX + rect.left : e.clientX;
                const y = rect ? e.clientY + rect.top : e.clientY;
                menu.style.left = x + 'px';
                menu.style.top = y + 'px';
                document.body.appendChild(menu);

                setTimeout(() => {
                  const close = (ev: MouseEvent) => {
                    if (!menu.contains(ev.target as Node)) {
                      menu.remove();
                      document.removeEventListener('click', close);
                    }
                  };
                  document.addEventListener('click', close);
                }, 0);
              }, { capture: true });
            });
          },
        }}
      />
    </div>
  );
});

TinyMCEEditor.displayName = 'TinyMCEEditor';
export default TinyMCEEditor;
